import {Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions} from "@headlessui/react";
import {ChevronUpDownIcon} from "@heroicons/react/20/solid";
import {XMarkIcon} from "@heroicons/react/24/outline";
import {useState} from "react";
import {CreditCardStatementIcon} from "../../../../../../../components/general/CustomIcons/CreditCardStatementIcon.tsx";
import {cleanDocumentFileName} from "../../../../../../../lib/TFICLib.ts";
import {formatDate} from "../../../../../../../lib/utils.ts";
import type {CreditCardStatementDocumentOption} from "../../types.ts";

interface CreditCardStatementSelectorProps {
    documents: CreditCardStatementDocumentOption[];
    selectedDocumentId?: string;
    onSelect: (documentId: string) => void;
    menuClassName?: string;
}

function matchesQuery(document: CreditCardStatementDocumentOption, query: string): boolean {
    const haystack = [
        cleanDocumentFileName(document.fileName),
        formatDate(document.createdAt),
    ].join(" ").toLowerCase();

    return haystack.includes(query.toLowerCase());
}

export function CreditCardStatementSelector({
    documents,
    selectedDocumentId,
    onSelect,
    menuClassName = "w-[32rem]",
}: CreditCardStatementSelectorProps) {
    const [query, setQuery] = useState<string>("");

    const selectedDocument = documents.find((document) => document.id === selectedDocumentId) ?? null;

    const filteredDocuments = query.trim() === ""
        ? documents
        : documents.filter((document) => matchesQuery(document, query));

    return (
        <Combobox
            as="div"
            value={selectedDocument}
            by="id"
            immediate
            onChange={(document: CreditCardStatementDocumentOption | null) => {
                setQuery("");
                onSelect(document ? document.id : "");
            }}
        >
            <div className="relative">
                <ComboboxInput
                    placeholder="Zoek een creditcard afschrift"
                    className="block h-[38px] w-full rounded-md bg-white py-1.5 pl-3 pr-12 text-base text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 outline-none placeholder:text-gray-400 focus:ring-2 focus:ring-primary sm:text-sm"
                    onChange={(event) => setQuery(event.target.value)}
                    onBlur={() => setQuery("")}
                    displayValue={(document: CreditCardStatementDocumentOption | null) =>
                        document ? cleanDocumentFileName(document.fileName) : ""
                    }
                />
                {selectedDocument && (
                    <button
                        type="button"
                        onClick={() => {
                            setQuery("");
                            onSelect("");
                        }}
                        className="absolute inset-y-0 right-6 flex items-center px-2 focus:outline-none"
                        aria-label="Selectie wissen"
                    >
                        <XMarkIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </button>
                )}
                <ComboboxButton className="absolute inset-y-0 right-0 flex items-center px-2 focus:outline-none">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </ComboboxButton>

                <ComboboxOptions
                    className={`${menuClassName} absolute z-50 mt-1 max-h-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm`}
                >
                    {filteredDocuments.length === 0 ? (
                        <div className="cursor-default select-none px-3 py-2 text-sm text-gray-500">
                            Geen creditcard afschriften gevonden
                        </div>
                    ) : (
                        filteredDocuments.map((document) => (
                            <ComboboxOption
                                key={document.id}
                                value={document}
                                className={({focus}) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                        focus ? "bg-primary text-white" : "text-gray-900"
                                    }`
                                }
                            >
                                {({focus}) => (
                                    <div className="flex items-center gap-3">
                                        <CreditCardStatementIcon
                                            className={`h-6 w-6 shrink-0 ${focus ? "text-white" : "text-gray-400"}`}
                                        />
                                        <div className="flex flex-col">
                                            <span className={`text-sm font-medium ${focus ? "text-white" : ""}`}>
                                                {cleanDocumentFileName(document.fileName)}
                                            </span>
                                            <span className={`whitespace-nowrap text-xs ${focus ? "text-blue-100" : "text-gray-500"}`}>
                                                Geüpload {formatDate(document.createdAt)}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </ComboboxOption>
                        ))
                    )}
                </ComboboxOptions>
            </div>
        </Combobox>
    );
}
